import React, { useEffect, useLayoutEffect } from 'react';
import { Tabs } from 'antd';
import ProductsTab from 'screens/Applications&WorkOrders/Components/DetailsTabs/components/ProductsTab';
import AppliedAreaTab from 'screens/Applications&WorkOrders/Components/DetailsTabs/components/AppliedAreaTab';
import DetailsTab from 'screens/Applications&WorkOrders/Components/DetailsTabs/components/DetailsTab';
import WeatherTab from 'screens/Applications&WorkOrders/Components/DetailsTabs/components/WeatherTab';
import NotesTab from 'screens/Applications&WorkOrders/Components/DetailsTabs/components/NotesTab';
import LinkedRecordsTab from 'screens/Applications&WorkOrders/Components/DetailsTabs/components/LinkedRecordsTab';
import { LinkedRecordsState } from 'screens/Applications&WorkOrders/Components/DetailsTabs/components/LinkedRecordsTab/types';
import {
  FullProduct,
  TankInformation
} from 'screens/Applications&WorkOrders/Applications/Hooks/useApplication';
import { OperationPropertyVO } from 'screens/Applications&WorkOrders/Components/hooks/useOperationProperties';
import { OperationType } from 'components/RecordsListView/constants';
import useActiveTab from 'screens/Applications&WorkOrders/Components/hooks/useActiveTab';
import { OperationDetails } from '../types';
import {
  OPERATION_DETAILS_TABS_WRAPPER_CLASS,
  TAB_KEYS,
  TAB_LABELS,
  TAB_TYPE
} from './constants';
import './index.css';

export interface DetailsTabsProps {
  type?: OperationType;
  details: OperationDetails;
  products: FullProduct[];
  propertiesData: OperationPropertyVO[];
  tankInformation?: TankInformation;
  isLoadingProducts?: boolean;
  isLoadingProperties?: boolean;
  showWeatherTab?: boolean;
  showNotesTab?: boolean;
  /**
   * Overrides the type-derived sessionStorage key. Callers embedding DetailsTabs
   * outside the main Applications/Work Orders screens (e.g. the Linked Records
   * drawer) pass a unique key so their tab state does not bleed into those
   * screens' persisted state.
   */
  storageKey?: string;
  linkedRecordsCount?: number;
  workOrderId?: string;
  linkedRecords?: LinkedRecordsState;
  /**
   * Notified with the current active tab key on mount and on every tab
   * switch, so the parent can defer work tied to a specific tab (e.g. only
   * fetching Linked Records once that tab is actually selected) instead of
   * eagerly running it for every tab as soon as the panel opens.
   */
  onActiveTabChange?: (tabKey: string) => void;
  /**
   * Element whose height caps the tables' scrollable body. Embedders whose tabs
   * sit in a container shorter than the page — e.g. the Linked Records overview
   * drawer — must point this at the element that actually claims the available
   * height. Left at the default, the tables measure this component's own root,
   * which shrink-wraps them, and the feedback loop settles at a couple of rows.
   */
  tablesWrapperSelector?: string;
}

const DetailsTabs = ({
  type = OperationType.APPLICATION,
  details,
  products,
  propertiesData,
  tankInformation,
  isLoadingProducts = false,
  isLoadingProperties = false,
  showWeatherTab = false,
  showNotesTab = true,
  storageKey,
  linkedRecordsCount,
  workOrderId,
  linkedRecords,
  tablesWrapperSelector,
  onActiveTabChange
}: DetailsTabsProps) => {
  const resolvedStorageKey =
    storageKey ??
    (type === OperationType.APPLICATION
      ? 'application-details'
      : 'work-order-details');
  const { activeTab, setActiveTab } = useActiveTab(resolvedStorageKey);

  const weatherInfo = {
    weather: (details as any)?.weather || {},
    startDate: details.startDate,
    endDate: details.endDate
  };

  const notesInfo = {
    notes: details.notes || ''
  };

  const items = [
    {
      key: TAB_KEYS.PRODUCTS,
      label: TAB_LABELS.PRODUCTS,
      children: (
        <ProductsTab
          products={products}
          tankInformation={tankInformation}
          isLoading={isLoadingProducts}
          wrapperSelector={tablesWrapperSelector}
        />
      )
    },
    {
      key: TAB_KEYS.APPLIED_AREA,
      label: TAB_LABELS.APPLIED_AREA,
      children: (
        <AppliedAreaTab
          propertiesData={propertiesData}
          cropSeasonIds={details.cropSeasonIds || []}
          isLoading={isLoadingProperties}
          wrapperSelector={tablesWrapperSelector}
        />
      )
    },
    {
      key: TAB_KEYS.DETAILS,
      label: TAB_LABELS.DETAILS,
      children: <DetailsTab applicationDetails={details as any} type={type} />
    },
    ...(showWeatherTab
      ? [
          {
            key: TAB_KEYS.WEATHER,
            label: TAB_LABELS.WEATHER,
            children: <WeatherTab weatherInfo={weatherInfo} />
          }
        ]
      : []),
    ...(linkedRecordsCount !== undefined &&
    linkedRecordsCount > 0 &&
    type === OperationType.WORK_ORDER &&
    workOrderId &&
    linkedRecords
      ? [
          {
            key: TAB_KEYS.LINKED_RECORDS,
            label: `Linked Records (${linkedRecordsCount})`,
            children: (
              <LinkedRecordsTab
                workOrderId={workOrderId}
                linkedRecords={linkedRecords}
              />
            )
          }
        ]
      : []),
    ...(showNotesTab
      ? [
          {
            key: TAB_KEYS.NOTES,
            label: TAB_LABELS.NOTES,
            children: <NotesTab notesInfo={notesInfo} type={type} />
          }
        ]
      : [])
  ];

  /**
   * The active tab persists across records (`useActiveTab` is keyed by
   * storage key, not record id, and `DetailsTabs` isn't remounted between
   * them). Landing on a record whose tab set doesn't include the previously
   * active key — e.g. Linked Records was active and the new Work Order has
   * none, or Notes is hidden — must fall back to a tab that actually exists,
   * or antd renders no matching pane at all. The effect below writes the
   * fallback back through `setActiveTab` so the stored key doesn't stay stale
   * and resurface a hidden tab if a later record brings it back.
   */
  const isActiveTabAvailable = items.some(item => item.key === activeTab);
  const activeTabKey = isActiveTabAvailable ? activeTab : TAB_KEYS.PRODUCTS;

  useEffect(() => {
    if (!isActiveTabAvailable) {
      setActiveTab(TAB_KEYS.PRODUCTS);
    }
  }, [isActiveTabAvailable, setActiveTab]);

  /**
   * Layout effect, not a regular effect: the parent gates the Linked Records
   * fetch on this callback, and a passive effect would let the browser paint
   * an empty tab pane first (no data yet, since the parent hasn't re-gated
   * the fetch) before the loading state appears. Running before paint keeps
   * that gating change in the same commit as the tab switch.
   */
  useLayoutEffect(() => {
    onActiveTabChange?.(activeTabKey);
  }, [activeTabKey, onActiveTabChange]);

  return (
    <div
      data-testid="operation-details-tabs"
      className={`${OPERATION_DETAILS_TABS_WRAPPER_CLASS} mt-4`}
    >
      <Tabs
        activeKey={activeTabKey}
        onChange={setActiveTab}
        items={items}
        type={TAB_TYPE}
        className="leading-5"
      />
    </div>
  );
};

export default DetailsTabs;
